import { CartLinesQueryResult } from '@graphql/queries/__generated__/cart.generated'
import { Divider, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useEffect, useState } from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { getProductUrl } from '../../../urls'
import CartLinePrice from './CartLinePrice'
import CartlineQtyWidget from './CartlineQtyWidget'
import Pagination from '@mui/material/Pagination'
import { useTranslation } from 'react-i18next'
import PaginateByButton from '@components/PaginateByButton'
import FormattedPrice from '@components/FormattedPrice'

type CartLineNode = CartLinesQueryResult['data']['cartLines']['results'][0]

interface CartLinesProps {
  cartLinesQueryResult: CartLinesQueryResult
  lineLoading: number | null
  isAuthenticated: boolean

  handleEditQty(linePk: number, qty: number): Promise<void>

  handleRemoveLine(linePk: number): Promise<void>

  handleSaveForLaterLine(productPk: number, linePk: number): Promise<void>

  handleEditLine(linePk: number): Promise<void>

  onClickProduct(specId: string | number, slug: string): void

  page: number
  paginateBy: number

  setPage(page: number): void

  setPaginateBy(paginateBy: number): void
}

const CartLines: React.FC<CartLinesProps> = ({
  cartLinesQueryResult,
  lineLoading,
  handleEditQty,
  handleRemoveLine,
  handleEditLine,
  handleSaveForLaterLine,
  onClickProduct,
  isAuthenticated,
  page,
  setPage,
  paginateBy,
  setPaginateBy,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'))
  const [lines, setLines] = useState<CartLineNode[]>([])

  useEffect(() => {
    if (!cartLinesQueryResult.loading || cartLinesQueryResult.data) {
      setLines(cartLinesQueryResult.data?.cartLines.results || [])
    }
  }, [cartLinesQueryResult.data, cartLinesQueryResult.loading])

  const handleClickProduct = (specId: number, slug: string) => e => {
    e.preventDefault()
    onClickProduct(specId, slug)
  }

  const numPages = Math.ceil((cartLinesQueryResult.data?.cartLines?.totalCount || 0) / (paginateBy || 10))
  return (
    <>
      {smUp && (
        <>
          <div className='flex items-center py-6 px-12 uppercase text-gray-700 leading-18 text-xs'>
            <span className='flex-1'>{t('item', 'Item')}</span>
            <span className='ml-12 w-90 text-right'>{t('total', 'Total')}</span>
            <span className='ml-12 w-100 text-center'>{t('qty', 'Qty')}</span>
          </div>
          <div className='hidden sm:block'>
            <Divider />
          </div>
        </>
      )}
      {!lines.length && (
        <div className='py-40 text-center text-lg relative'>
          <Loader
            className='spinner spinner--small'
            type='ThreeDots'
            color='#333'
            visible={cartLinesQueryResult.loading}
            height={100}
            width={100}
          />
          {!cartLinesQueryResult.loading && <span>{t('yourShoppingBagIsEmpty', 'Your Shopping Bag is empty.')}</span>}
        </div>
      )}
      {!!lines.length && (
        <Loader
          className='spinner spinner--small'
          type='ThreeDots'
          color='#333'
          visible={cartLinesQueryResult.loading}
          height={100}
          width={100}
        />
      )}
      <ul className='p-0 list-none'>
        {lines.map((line, idx) => {
          const variant = line.variant
          const product = variant.product
          const spec = product.siteSpecifics[0]
          const itemPrice = line.supplyParts.length
            ? line.supplyParts.reduce(
                (prev, curr) => {
                  return {
                    currency: line.unitPrice.currency,
                    discount: prev.discount + curr.unitPrice.discount * curr.quantity,
                    undiscount: prev.undiscount + curr.unitPrice.undiscount * curr.quantity,
                  }
                },
                { currency: line.unitPrice.currency, discount: 0, undiscount: 0 }
              )
            : {
                currency: line.unitPrice.currency,
                discount: line.unitPrice.discount,
                undiscount: line.unitPrice.undiscount,
              }
          const isSale = itemPrice.undiscount !== itemPrice.discount
          return (
            <li key={idx} className='relative'>
              <Loader
                className='spinner spinner--small'
                type='ThreeDots'
                color='#333'
                visible={lineLoading === line.pk}
                height={100}
                width={100}
              />
              <div className='p-12 flex'>
                <Link
                  to={getProductUrl(spec?.slug, spec?.pk)}
                  onClick={handleClickProduct(spec?.pk, spec?.slug)}
                  className='mr-12 w-80 min-w-80 sm:w-120 sm:min-w-120'
                >
                  <img className='max-w-full' src={variant.thumbnailUrl || spec?.thumbnailUrl} alt={spec?.name} />
                </Link>
                <div className='flex-1'>
                  <div className='flex text-13 leading-18'>
                    <div className='w-full flex-1'>
                      <Link
                        to={getProductUrl(spec?.slug, spec?.pk)}
                        onClick={handleClickProduct(spec?.pk, spec?.slug)}
                        className='no-underline leading-6 break-words'
                      >
                        {spec?.name}
                      </Link>
                      <div className='mt-5 break-all line-clamp-1'>
                        <span className='font-bold uppercase'>{t('sku', 'SKU')}: </span>
                        <span title={variant.sku}>{variant.sku}</span>
                      </div>
                      {variant.properties.map((prop, idx) => (
                        <div key={idx}>
                          <span className='font-bold uppercase'>{prop.attributeName}: </span>
                          <span>{prop.valueName}</span>
                        </div>
                      ))}
                      <div className='break-words'>
                        <span className='font-bold uppercase'>{t('productPrice', 'Product price')}: </span>
                        <span className={isSale ? 'line-through' : ''}>
                          <FormattedPrice amount={itemPrice.undiscount} currency={itemPrice.currency} />
                        </span>
                        {isSale && (
                          <span className={`text-red`}>
                            {' '}
                            <FormattedPrice amount={itemPrice.discount} currency={itemPrice.currency} />
                          </span>
                        )}
                      </div>
                      <div>
                        <span className='font-bold uppercase'>{t('qty', 'Qty')}: </span>
                        <span>{line.quantity}</span>
                      </div>
                    </div>
                    <div className='ml-12 w-auto sm:w-90 text-right flex flex-col mt-4'>
                      <CartLinePrice line={line} qty={line.quantity} />
                    </div>
                    {smUp && (
                      <div className='ml-12 w-100 text-center'>
                        <CartlineQtyWidget
                          qty={line.quantity}
                          stockQty={variant.stockQuantity}
                          canEdit={!line.supplyParts.length && variant.shippingRequired}
                          handleEditQty={qty => handleEditQty(line.pk, qty)}
                        />
                      </div>
                    )}
                  </div>
                  {!!line.supplyParts.length && (
                    <>
                      <div className='font-bold mt-10 flex justify-between items-center'>
                        <span>{t('kitsContent', "KIT's CONTENT")}:</span>
                      </div>
                      {line.supplyParts.map((part, idx) => {
                        const partSpec = part.variant.product.specifics[0]
                        return (
                          <div key={idx} className='text-sm'>
                            <span>{idx + 1}. </span>
                            <span>
                              <Link
                                to={getProductUrl(partSpec?.slug, partSpec?.pk)}
                                onClick={handleClickProduct(partSpec?.pk, partSpec?.slug)}
                                className='leading-6 break-words'
                              >
                                {partSpec?.name}
                              </Link>
                            </span>
                            <span> {t('xQtyPcs', { defaultValue: 'x {{quantity}}pcs', quantity: part.quantity })}</span>
                            <span>
                              {' '}
                              - <CartLinePrice part={part} qty={1} />
                            </span>
                          </div>
                        )
                      })}
                    </>
                  )}
                  {smUp && (
                    <div className='-mx-8 mt-5'>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center flex-1'>
                          {line.variant.isSupply && line.variant.isCustomizable && (
                            <span>
                              <Button
                                className='transparent'
                                onClick={() => handleEditLine(line.pk)}
                                color='secondary'
                                fullWidth
                                disableRipple
                              >
                                <span className='normal-case'>{t('button.customizeTheKit', 'Customize The KIT')}</span>
                              </Button>
                            </span>
                          )}
                          {isAuthenticated && (
                            <span>
                              <Button
                                className='transparent'
                                onClick={() => handleSaveForLaterLine(product.pk, line.pk)}
                                color='secondary'
                                fullWidth
                                disableRipple
                              >
                                <span className='normal-case whitespace-nowrap'>
                                  {t('button.saveForLater', 'Save for Later')}
                                </span>
                              </Button>
                            </span>
                          )}
                        </div>
                        <span>
                          <Button
                            className='transparent'
                            onClick={() => handleRemoveLine(line.pk)}
                            color='warning'
                            fullWidth
                            disableRipple
                          >
                            <span className='normal-case'>{t('button.remove', 'Remove')}</span>
                          </Button>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {!smUp && (
                <div className='flex items-center flex-wrap justify-between mt-5 pl-4 pr-12 pb-12'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center flex-1'>
                      {line.variant.isSupply && line.variant.isCustomizable && (
                        <span>
                          <Button
                            className='transparent'
                            onClick={() => handleEditLine(line.pk)}
                            color='secondary'
                            fullWidth
                            disableRipple
                          >
                            <span className='normal-case'>{t('button.customizeTheKit', 'Customize The KIT')}</span>
                          </Button>
                        </span>
                      )}
                      {isAuthenticated && (
                        <span>
                          <Button
                            className='transparent'
                            onClick={() => handleSaveForLaterLine(product.pk, line.pk)}
                            color='secondary'
                            fullWidth
                            disableRipple
                          >
                            <span className='normal-case whitespace-nowrap'>
                              {t('button.saveForLater', 'Save for Later')}
                            </span>
                          </Button>
                        </span>
                      )}
                    </div>
                    <span>
                      <Button
                        className='transparent'
                        onClick={() => handleRemoveLine(line.pk)}
                        color='warning'
                        fullWidth
                        disableRipple
                      >
                        <span className='normal-case'>{t('button.remove', 'Remove')}</span>
                      </Button>
                    </span>
                  </div>
                  <div className='w-100 text-center'>
                    {!line.supplyParts.length && variant.shippingRequired && (
                      <CartlineQtyWidget
                        qty={line.quantity}
                        stockQty={variant.stockQuantity}
                        canEdit={!line.supplyParts.length && variant.shippingRequired}
                        handleEditQty={qty => handleEditQty(line.pk, qty)}
                      />
                    )}
                  </div>
                </div>
              )}
              {idx !== lines.length - 1 && <Divider />}
            </li>
          )
        })}
      </ul>
      <div className='flex items-center justify-center pb-10'>
        {numPages > 1 && (
          <Pagination
            count={numPages}
            page={page || 1}
            size='small'
            onChange={(e, page) => {
              setPage(page)
            }}
            siblingCount={1}
            shape='rounded'
          />
        )}
      </div>
      <div className='flex justify-end w-full relative pr-10 pb-10'>
        <PaginateByButton paginateBy={paginateBy} setPaginateBy={setPaginateBy} />
      </div>
    </>
  )
}

export default CartLines
